import React, { useCallback, useEffect, useState } from 'react';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useBlockActionDispatchAndGenericActions, useBlockId } from '../../components/blocks/BlockState';
import {
  AccountManagementConstants,
  useAccountManagementData,
  AccountManagementDataState,
  isAccountManagementTab,
} from './state/AccountManagementState';
import { AccountManagementBlockAction, AccountManagementBlockActions } from './state/AccountManagementBlockAction';
import { AccountDetails } from './AccountDetails';
import { FinancialDetails } from './FinancialDetails';
import { AccountDisclosures } from './AccountDisclosures';
import { useStateSelector } from '../../data/global/dataSelectors';
import { Beneficiaries } from './Beneficiaries';
import { AccountManagementDataActions } from '../../data/global/actions/AccountManagementActions';
import { AccountSecurity } from './Security';
import { Preferences } from './Preferences';

const StyledAccountManagementContent = styled.div<{ tabsWrap: boolean }>`
  width: 100%;
  /* Same ~555px floor used for Security layout before TBFE revert; cap at parent width */
  min-width: min(555px, 100%);
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;

  .tabs-wrapper {
    display: flex;
    flex-wrap: auto;
  }

  .nav-tabs-pill .nav-tabs {
    padding: ${(props) => props.tabsWrap ? '10px 40px' : '1px'};
  }
`;

const {
  clientAcc,
  clientFin,
  clientDiscl,
  clientPref,
  clientBeneficiaries,
  security,
} = AccountManagementConstants.tabs;

export const AccountManagementContent: React.FunctionComponent<{}> = ({}) => {
  const blockId = useBlockId();
  const dispatch = useDispatch();
  const accountId = useStateSelector(s => s.account.accountId);
  const retirementAccount = useStateSelector(
    s => s.accountManagement.accountManagementDetails.details.retirementAccountType
  );
  const isRetirementAccount = retirementAccount && retirementAccount !== '' ? true : false;
  const tab = useAccountManagementData(s => s.tab || 'clientAcc');
  const [, blockDispatch] = useBlockActionDispatchAndGenericActions<
    AccountManagementDataState,
    AccountManagementBlockAction
  >();
  const setTab = useCallback(
    (ev: string) => {
      if (isAccountManagementTab(ev)) {
        blockDispatch(AccountManagementBlockActions.setTab(ev));
      }
    },
    [blockDispatch]
  );

  const [tabsWrap, setTabsWrap] = useState<boolean>(false);

  useEffect(() => {
    if (accountId) {
      dispatch(AccountManagementDataActions.requestPendingAccountManagementUpdates({ accountId }));
    }
  }, [accountId, dispatch]);

  useEffect(() => {
    const blockElement = document.getElementById(`block-${blockId}`);

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect.width < 696) {
          setTabsWrap(true);
        } else {
          setTabsWrap(false);
        }
      })
    });

    if (blockElement) {
      resizeObserver.observe(blockElement);
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <StyledAccountManagementContent tabsWrap={tabsWrap}>
      <div className="nav-tabs-pill">
        <Tabs defaultActiveKey={tab} id="notification-tabs" onSelect={(ev: string) => setTab(ev)}>
          <Tab eventKey={clientAcc} title="Personal Info">
            {tab === clientAcc && <AccountDetails />}
          </Tab>
          <Tab eventKey={clientFin} title="Investing Profile">
            {tab === clientFin && <FinancialDetails />}
          </Tab>
          <Tab eventKey={clientDiscl} title="Account Disclosures">
            {tab === clientDiscl && <AccountDisclosures />}
          </Tab>
          <Tab eventKey={clientPref} title="Preferences">
            {tab === clientPref && <Preferences />}
          </Tab>
          <Tab eventKey={security} title="Security">
            {tab === security && <AccountSecurity />}
          </Tab>
          {isRetirementAccount && (
            <Tab eventKey={clientBeneficiaries} title="Beneficiaries">
              {tab === clientBeneficiaries && <Beneficiaries />}
            </Tab>
          )}
        </Tabs>
      </div>
    </StyledAccountManagementContent>
  );
};
